/**
 * PipelineProgress Component
 *
 * Real-time progress visualization for pipeline phases.
 *
 * Part of Story 54: Automated AEO Pipeline with Pilot Mode and Impact Propagation
 */

import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Play,
  Pause,
  SkipForward,
  RefreshCw,
} from 'lucide-react';
import {
  getPipelineDashboard,
  approvePilot,
  rejectPilot,
  resumePipeline,
  approvePropagation,
  skipPropagation,
  cancelPipeline,
  PipelineDashboard,
  PipelineState,
  PhaseProgress,
  PipelineAlert,
  AEOStatusCounts,
} from '@/services/pipelineService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/auth-context';
import { formatDistanceToNow, format } from 'date-fns';

interface PipelineProgressProps {
  runId: string;
  onRefresh?: () => void;
  onTabChange?: (tab: string) => void;
}

const stateConfig: Record<
  PipelineState,
  { icon: React.ReactNode; color: string; description: string }
> = {
  CONFIGURING: {
    icon: <Clock className="h-5 w-5" />,
    color: 'text-gray-500',
    description: 'Configuring pipeline parameters',
  },
  PILOT_RUNNING: {
    icon: <Loader2 className="h-5 w-5 animate-spin" />,
    color: 'text-blue-500',
    description: 'Running pilot phase on sample products',
  },
  PILOT_REVIEW: {
    icon: <AlertTriangle className="h-5 w-5" />,
    color: 'text-yellow-500',
    description: 'Pilot complete. Review results and approve to continue.',
  },
  PILOT_REJECTED: {
    icon: <XCircle className="h-5 w-5" />,
    color: 'text-red-500',
    description: 'Pilot was rejected. Adjust configuration and retry.',
  },
  FULL_RUNNING: {
    icon: <Loader2 className="h-5 w-5 animate-spin" />,
    color: 'text-blue-600',
    description: 'Processing all products in category',
  },
  FULL_PAUSED: {
    icon: <Pause className="h-5 w-5" />,
    color: 'text-orange-500',
    description: 'Pipeline paused. Review looped products.',
  },
  IMPACT_ANALYSIS: {
    icon: <Loader2 className="h-5 w-5 animate-spin" />,
    color: 'text-purple-500',
    description: 'Analyzing impact of new research',
  },
  IMPACT_REVIEW: {
    icon: <AlertTriangle className="h-5 w-5" />,
    color: 'text-yellow-600',
    description: 'Review impacted products before propagation.',
  },
  PROPAGATION_RUNNING: {
    icon: <Loader2 className="h-5 w-5 animate-spin" />,
    color: 'text-indigo-500',
    description: 'Regenerating impacted products',
  },
  COMPLETED: {
    icon: <CheckCircle className="h-5 w-5" />,
    color: 'text-green-500',
    description: 'Pipeline completed successfully',
  },
  FAILED: {
    icon: <XCircle className="h-5 w-5" />,
    color: 'text-red-600',
    description: 'Pipeline failed',
  },
};

function PhaseProgressBar({
  label,
  progress,
  active,
}: {
  label: string;
  progress?: any; // Backend sends different structure
  active: boolean;
}) {
  if (!progress || progress.total === 0) {
    return (
      <div className="space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">{label}</span>
          <span className="text-muted-foreground">Not started</span>
        </div>
        <Progress value={0} className="h-2" />
      </div>
    );
  }

  // Backend sends: total, completed, failed, pending, percentage
  const total = progress.total || 0;
  const completed = progress.completed || 0;
  const failed = progress.failed || 0;
  const pending = progress.pending || 0;
  const percentage = progress.percentage || 0;

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className={active ? 'font-medium' : 'text-muted-foreground'}>{label}</span>
        <span className={active ? '' : 'text-muted-foreground'}>
          {completed + failed} / {total} ({percentage}%)
        </span>
      </div>
      <Progress value={percentage} className="h-2" />
      <div className="flex gap-4 text-xs text-muted-foreground">
        {completed > 0 && (
          <span className="text-green-600">{completed} completed</span>
        )}
        {failed > 0 && (
          <span className="text-red-600">{failed} failed</span>
        )}
        {pending > 0 && (
          <span className="text-blue-600">{pending} pending</span>
        )}
      </div>
    </div>
  );
}

interface AlertProduct {
  id: string;
  name?: string;
  iterations?: number;
  image_url?: string;
}

interface ExtendedAlert {
  type?: string;
  title?: string;
  message?: string;
  action?: string;
  products?: AlertProduct[];
}

function AEOStatusBreakdown({
  counts,
  onNavigate,
}: {
  counts?: AEOStatusCounts;
  onNavigate: (status: string) => void;
}) {
  if (!counts || counts.total === 0) {
    return null;
  }

  return (
    <Card className="mt-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <CheckCircle className="h-4 w-4" />
          Content Review Status
        </CardTitle>
        <CardDescription>
          Click to view in Content Review Dashboard
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Pending Review */}
          <button
            onClick={() => onNavigate('pending')}
            className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors text-left"
          >
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {counts.pendingReview}
            </div>
            <div className="text-xs text-blue-600/80 dark:text-blue-400/80">
              Pending Review
            </div>
          </button>

          {/* Needs Research */}
          <button
            onClick={() => onNavigate('needs_research')}
            className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors text-left"
          >
            <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
              {counts.needs_research}
            </div>
            <div className="text-xs text-amber-600/80 dark:text-amber-400/80">
              Needs Research
            </div>
          </button>

          {/* Approved */}
          <button
            onClick={() => onNavigate('approved')}
            className="p-3 rounded-lg bg-green-50 dark:bg-green-950/30 hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors text-left"
          >
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {counts.approved}
            </div>
            <div className="text-xs text-green-600/80 dark:text-green-400/80">
              Approved
            </div>
          </button>

          {/* Rejected */}
          <button
            onClick={() => onNavigate('rejected')}
            className="p-3 rounded-lg bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors text-left"
          >
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {counts.rejected}
            </div>
            <div className="text-xs text-red-600/80 dark:text-red-400/80">
              Rejected
            </div>
          </button>
        </div>

        {/* Total summary */}
        <div className="mt-3 pt-3 border-t text-sm text-muted-foreground">
          Total AEO content generated: {counts.total}
        </div>
      </CardContent>
    </Card>
  );
}

function AlertItem({ alert, onNavigate }: { alert: ExtendedAlert; onNavigate?: (tab: string) => void }) {
  // Safely extract string values, handling cases where objects might be passed
  const alertType = typeof alert?.type === 'string' ? alert.type : 'warning';
  const alertTitle = typeof alert?.title === 'string' ? alert.title : 'Alert';
  const alertMessage = typeof alert?.message === 'string' ? alert.message : '';
  const alertAction = typeof alert?.action === 'string' ? alert.action : undefined;
  const alertProducts = Array.isArray(alert?.products) ? alert.products : [];

  const variants: Record<string, 'default' | 'destructive'> = {
    error: 'destructive',
    warning: 'default',
    info: 'default',
    success: 'default',
  };

  const icons: Record<string, React.ReactNode> = {
    error: <XCircle className="h-4 w-4" />,
    warning: <AlertTriangle className="h-4 w-4" />,
    info: <Clock className="h-4 w-4" />,
    success: <CheckCircle className="h-4 w-4" />,
  };

  // Get action guidance based on alert action type
  const getActionGuidance = (action?: string) => {
    switch (action) {
      case 'review_loops':
        return {
          text: 'Go to Loop Alerts tab to diagnose and fix these products',
          tab: 'loops',
        };
      case 'resume':
        return {
          text: 'Click "Resume Pipeline" after reviewing looped products',
          tab: null,
        };
      case 'review_failures':
        return {
          text: 'Review failed products in the Pilot Review tab',
          tab: 'pilot',
        };
      default:
        return null;
    }
  };

  const guidance = getActionGuidance(alertAction);

  return (
    <Alert variant={variants[alertType] || 'default'}>
      {icons[alertType]}
      <AlertTitle>{alertTitle}</AlertTitle>
      <AlertDescription className="space-y-2">
        <p>{alertMessage}</p>
        {/* Show product names with images if available */}
        {alertProducts.length > 0 && (
          <div className="mt-2 text-sm">
            <p className="font-medium mb-2">Affected products:</p>
            <div className="space-y-2">
              {alertProducts.map((p) => {
                const productId = typeof p?.id === 'string' ? p.id : '';
                const productName = typeof p?.name === 'string' ? p.name : productId;
                const iterations = typeof p?.iterations === 'number' ? p.iterations : 0;
                const imageUrl = typeof p?.image_url === 'string' ? p.image_url : undefined;

                return (
                  <div key={productId} className="flex items-center gap-2">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={productName}
                        className="w-8 h-8 rounded object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded bg-muted flex items-center justify-center text-xs">
                        ?
                      </div>
                    )}
                    <span className="flex-1">{productName}</span>
                    <span className="text-muted-foreground">({iterations} iterations)</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* Show action guidance */}
        {guidance && (
          <div className="mt-2 pt-2 border-t border-border">
            {guidance.tab && onNavigate ? (
              <button
                onClick={() => onNavigate(guidance.tab!)}
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                {guidance.text} →
              </button>
            ) : (
              <p className="text-sm text-muted-foreground">{guidance.text}</p>
            )}
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}

export function PipelineProgress({ runId, onRefresh, onTabChange }: PipelineProgressProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<PipelineDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const isSuperAdmin = user?.role === 'super_admin';

  // Navigate to Content Review Dashboard with specific status filter
  const navigateToContentReview = (status: string) => {
    const params = new URLSearchParams({
      contentType: 'product_aeo',
      status: status,
    });
    navigate(`/content-review?${params.toString()}`);
  };

  const loadDashboard = async () => {
    try {
      const data = await getPipelineDashboard(runId);
      setDashboard(data);
    } catch (error) {
      console.error('Failed to load pipeline dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();

    // Auto-refresh for active states
    const activeStates: PipelineState[] = [
      'PILOT_RUNNING',
      'FULL_RUNNING',
      'IMPACT_ANALYSIS',
      'PROPAGATION_RUNNING',
    ];

    if (dashboard && activeStates.includes(dashboard.state)) {
      const interval = setInterval(loadDashboard, 5000);
      return () => clearInterval(interval);
    }
  }, [runId, dashboard?.state]);

  const handleAction = async (action: string) => {
    setActionLoading(action);
    try {
      switch (action) {
        case 'approve_pilot':
          await approvePilot(runId);
          break;
        case 'reject_pilot':
          await rejectPilot(runId, 'Rejected by user');
          break;
        case 'resume':
          await resumePipeline(runId);
          break;
        case 'approve_propagation':
          await approvePropagation(runId);
          break;
        case 'skip_propagation':
          await skipPropagation(runId);
          break;
        case 'cancel':
          await cancelPipeline(runId, 'Cancelled by user');
          break;
      }
      await loadDashboard();
      onRefresh?.();
    } catch (error) {
      console.error(`Action ${action} failed:`, error);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!dashboard || !dashboard.state) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Failed to load pipeline data
        </CardContent>
      </Card>
    );
  }

  const config = stateConfig[dashboard.state];
  if (!config) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Unknown pipeline state: {dashboard.state}
        </CardContent>
      </Card>
    );
  }
  const isRunning = ['PILOT_RUNNING', 'FULL_RUNNING', 'IMPACT_ANALYSIS', 'PROPAGATION_RUNNING'].includes(
    dashboard.state
  );

  return (
    <div className="space-y-4">
      {/* State Header */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={config.color}>{config.icon}</div>
              <div>
                <CardTitle className="text-lg">{dashboard.state.replace(/_/g, ' ')}</CardTitle>
                <CardDescription>{config.description}</CardDescription>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={loadDashboard}>
                <RefreshCw className={`h-4 w-4 ${isRunning ? 'animate-spin' : ''}`} />
              </Button>
              {!['COMPLETED', 'FAILED'].includes(dashboard.state) && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleAction('cancel')}
                  disabled={actionLoading === 'cancel'}
                >
                  {actionLoading === 'cancel' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Cancel'
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Timing Info */}
          <div className="flex gap-6 text-sm text-muted-foreground mb-4">
            {dashboard.timing.started_at && (
              <span>
                Started {formatDistanceToNow(new Date(dashboard.timing.started_at), { addSuffix: true })}
              </span>
            )}
            {dashboard.timing.completed_at && (
              <span>
                Completed {format(new Date(dashboard.timing.completed_at), 'PPp')}
              </span>
            )}
            {/* LLM Cost - only visible to super admins */}
            {isSuperAdmin && dashboard.total_cost_usd != null && Number(dashboard.total_cost_usd) > 0 && (
              <span className="text-amber-600 font-medium">
                LLM Cost: ${Number(dashboard.total_cost_usd).toFixed(4)}
              </span>
            )}
          </div>

          {/* Phase Progress */}
          <div className="space-y-4">
            <PhaseProgressBar
              label="Pilot Phase"
              progress={dashboard.progress.pilot}
              active={['PILOT_RUNNING', 'PILOT_REVIEW'].includes(dashboard.state)}
            />
            <PhaseProgressBar
              label="Full Generation"
              progress={dashboard.progress.full}
              active={['FULL_RUNNING', 'FULL_PAUSED'].includes(dashboard.state)}
            />
            <PhaseProgressBar
              label="Propagation"
              progress={dashboard.progress.propagation}
              active={['PROPAGATION_RUNNING'].includes(dashboard.state)}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-6">
            {dashboard.state === 'PILOT_REVIEW' && (
              <>
                <Button
                  onClick={() => handleAction('approve_pilot')}
                  disabled={actionLoading === 'approve_pilot'}
                >
                  {actionLoading === 'approve_pilot' ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-1" />
                  )}
                  Approve Pilot
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleAction('reject_pilot')}
                  disabled={actionLoading === 'reject_pilot'}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Reject
                </Button>
              </>
            )}
            {dashboard.state === 'FULL_PAUSED' && (
              <Button
                onClick={() => handleAction('resume')}
                disabled={actionLoading === 'resume'}
              >
                {actionLoading === 'resume' ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Play className="h-4 w-4 mr-1" />
                )}
                Resume Pipeline
              </Button>
            )}
            {dashboard.state === 'IMPACT_REVIEW' && (
              <>
                <Button
                  onClick={() => handleAction('approve_propagation')}
                  disabled={actionLoading === 'approve_propagation'}
                >
                  {actionLoading === 'approve_propagation' ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-1" />
                  )}
                  Approve Propagation
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleAction('skip_propagation')}
                  disabled={actionLoading === 'skip_propagation'}
                >
                  <SkipForward className="h-4 w-4 mr-1" />
                  Skip Propagation
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* AEO Content Status Breakdown */}
      <AEOStatusBreakdown
        counts={dashboard.aeoStatusCounts}
        onNavigate={navigateToContentReview}
      />

      {/* Alerts */}
      {dashboard.alerts && dashboard.alerts.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Alerts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {dashboard.alerts.map((alert, index) => (
              <AlertItem key={index} alert={alert as ExtendedAlert} onNavigate={onTabChange} />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
